
import { Player } from "@/types";
import { GlassCard } from "@/components/ui/glass-card";

interface SelectedPlayersPodiumProps {
  selectedPlayers: string[];
  players: Player[];
}

const goldTileStyle: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, #FFF4B8 0%, #FFD24A 38%, #E8B400 100%)",
  borderTop: "1px solid #C9970B",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.85)",
  color: "#5A3A00",
};

const silverTileStyle: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, #F6F8FA 0%, #C9CFD6 50%, #8E97A1 100%)",
  borderTop: "1px solid #9AA3AD",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.85)",
  color: "#2A2F36",
};

const bronzeTileStyle: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, #F0B07F 0%, #B7783F 55%, #7C4A1E 100%)",
  borderTop: "1px solid #6E3F18",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
  color: "#3A1F08",
};

export function SelectedPlayersPodium({ selectedPlayers, players }: SelectedPlayersPodiumProps) {
  if (selectedPlayers.length === 0) {
    return (
      <GlassCard className="p-2 mb-2">
        <p className="text-sm text-muted-foreground text-center py-1">
          Allocate vote points 3,2,1
        </p>
      </GlassCard>
    );
  }

  const getPlayerInfo = (index: number) => {
    if (index >= selectedPlayers.length) return null;
    const playerId = selectedPlayers[index];
    return players.find(p => p.id === playerId) ?? null;
  };

  const firstPlace = getPlayerInfo(0);
  const secondPlace = getPlayerInfo(1);
  const thirdPlace = getPlayerInfo(2);

  return (
    <div className="mb-2 px-2">
      <div className="flex items-end justify-center h-16 mb-0 relative">
        {/* Second Place — Silver */}
        {secondPlace && (
          <div className="flex flex-col items-center mx-1 z-10 self-end pb-0">
            <div className="flex items-center justify-center h-6 pb-0.5">
              <span
                className="text-lg"
                style={{ filter: "drop-shadow(0 0 6px rgba(220,230,240,0.85))" }}
              >
                🥈
              </span>
            </div>
            <div className="relative">
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-3 rounded-full animate-podium-halo-silver"
              />
              <div
                className="relative w-20 h-10 rounded-t-md flex items-center justify-center overflow-hidden"
                style={silverTileStyle}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -translate-x-full animate-podium-shimmer-slow bg-gradient-to-r from-transparent via-white/60 to-transparent"
                />
                <span className="relative text-xs font-semibold text-center line-clamp-1 px-1">
                  {secondPlace.name}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* First Place — Gold */}
        {firstPlace && (
          <div className="flex flex-col items-center mx-1 z-20 self-end pb-0">
            <div className="flex items-center justify-center h-7 pb-0.5 relative">
              <span
                className="text-lg relative"
                style={{ filter: "drop-shadow(0 0 10px rgba(255,210,80,0.95))" }}
              >
                🥇
              </span>
            </div>
            <div className="relative">
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-4 rounded-full animate-podium-halo"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-1 rounded-md animate-podium-conic opacity-70 mix-blend-overlay"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-t-md animate-podium-gold-glow"
              />
              <div
                className="relative w-24 h-14 rounded-t-md flex items-center justify-center overflow-hidden"
                style={goldTileStyle}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -translate-x-full animate-podium-shimmer bg-gradient-to-r from-transparent via-white/75 to-transparent"
                />
                <span className="relative text-xs font-bold tracking-wide text-center line-clamp-1 px-1 drop-shadow-[0_1px_0_rgba(255,255,255,0.5)]">
                  {firstPlace.name}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Third Place — Bronze */}
        {thirdPlace && (
          <div className="flex flex-col items-center mx-1 z-10 self-end pb-0">
            <div className="flex items-center justify-center h-6 pb-0.5">
              <span
                className="text-lg"
                style={{ filter: "drop-shadow(0 0 6px rgba(210,120,60,0.85))" }}
              >
                🥉
              </span>
            </div>
            <div className="relative">
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-3 rounded-full animate-podium-halo-bronze"
              />
              <div
                className="relative w-20 h-8 rounded-t-md flex items-center justify-center overflow-hidden"
                style={bronzeTileStyle}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -translate-x-full animate-podium-shimmer-slow bg-gradient-to-r from-transparent via-white/50 to-transparent"
                />
                <span className="relative text-xs font-semibold text-center line-clamp-1 px-1">
                  {thirdPlace.name}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
